import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AudioRecorder from '../components/AudioRecorder';
import api from '../services/api';
import './TeacherQuestionnaire.css';

const TeacherQuestionnaire = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Устанавливаем токен анкеты при загрузке компонента
  useEffect(() => {
    const questionnaireToken = sessionStorage.getItem('questionnaire_token');
    if (questionnaireToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${questionnaireToken}`;
    }
  }, []);
  const [isUploading, setIsUploading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [submitError, setSubmitError] = useState('');
  const today = new Date();
  const adultDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  const formatDate = (d) => d.toISOString().split('T')[0];
  const maxBirthDate = formatDate(adultDate);
  const maxTeachingDate = formatDate(today);
  const [formData, setFormData] = useState({
    // Шаг 1: Форма с вопросами
    gender: '',
    residenceCountry: '',
    country: '',
    education: '',
    adultExperience: '',
    childrenExperience: '',
    itExperience: '',
    itEnglishExperience: '',
    groupTeachingExperience: '',
    teachingStartDate: maxTeachingDate,
    birthDate: maxBirthDate,
    egeExperience: '',
    certificates: [],
    russianBankCard: '',
    consentToPersonalData: false,
    // Аудио записи
    testRecording: null,
    audioAnswers: {},
    cv: null,
  });

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (files) {
      const file = files[0];
      if (file && file.size > 10 * 1024 * 1024) {
        alert('Файл должен быть не больше 10 МБ');
        return;
      }
      setFormData(prev => ({ ...prev, [name]: file }));
      return;
    }
    if (type === 'checkbox') {
      if (name === 'certificates') {
        setFormData(prev => ({
          ...prev,
          certificates: checked
            ? [...prev.certificates, value]
            : prev.certificates.filter(c => c !== value)
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const uploadRecording = async (blob, key) => {
    const form = new FormData();
    form.append('question_key', key);
    form.append('file', blob, `${key}.webm`);
    const resp = await api.post('/auth/questionnaire/upload/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  };

  const handleAudioComplete = async (audioBlob, isValid, questionKey = null) => {
    // Пробное видео не сохраняем на сервер, только валидируем локально
    if (!questionKey) {
      setFormData(prev => ({
        ...prev,
        testRecording: { blob: audioBlob, isValid, recordId: null }
      }));
      return;
    }

    if (!isValid) {
      setFormData(prev => ({
        ...prev,
        audioAnswers: {
          ...prev.audioAnswers,
          [questionKey]: { blob: audioBlob, isValid, recordId: null }
        }
      }));
      return;
    }

    setIsUploading(true);
    try {
      const data = await uploadRecording(audioBlob, questionKey);
      setFormData(prev => ({
        ...prev,
        audioAnswers: {
          ...prev.audioAnswers,
          [questionKey]: { blob: audioBlob, isValid, recordId: data.id }
        }
      }));
    } catch (e) {
      console.error('Ошибка загрузки записи', e);
      alert('Не удалось загрузить запись, попробуйте еще раз.');
      setFormData(prev => ({
        ...prev,
        audioAnswers: {
          ...prev.audioAnswers,
          [questionKey]: { blob: audioBlob, isValid: false, recordId: null }
        }
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const isStep1Valid = () => {
    return (
      formData.gender &&
      formData.residenceCountry &&
      formData.country &&
      formData.education &&
      formData.adultExperience &&
      formData.childrenExperience &&
      formData.itExperience &&
      formData.itEnglishExperience &&
      formData.groupTeachingExperience &&
      formData.teachingStartDate &&
      formData.birthDate &&
      formData.egeExperience &&
      formData.certificates.length > 0 &&
      formData.russianBankCard
    );
  };

  const handleNext = () => {
    if (isUploading) {
      alert('Дождитесь окончания загрузки записи');
      return;
    }
    if (currentStep === 1) {
      // Проверка заполнения всех полей
      if (!isStep1Valid()) {
        alert('Пожалуйста, заполните все поля');
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Проверка тестовой записи
      if (!formData.testRecording || !formData.testRecording.isValid) {
        alert('Необходимо записать тестовую запись минимум 10 секунд');
        return;
      }
      setCurrentStep(3);
    } else if (currentStep >= 3 && currentStep <= 6) {
      // Проверка аудио ответа на вопрос
      const questionKey = `question${currentStep - 2}`;
      if (!formData.audioAnswers[questionKey] || !formData.audioAnswers[questionKey].isValid) {
        alert('Необходимо записать ответ минимум 10 секунд');
        return;
      }
      if (currentStep < 6) {
        setCurrentStep(currentStep + 1);
      } else {
        // Сразу переходим на финальную страницу
        setCurrentStep(7);
        setSubmitStatus('loading');
        // Отправляем анкету в фоне
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitStatus('loading');
      setSubmitError('');
      
      const form = new FormData();
      // добавляем записи
      const recordIds = [];
      // Пробное видео не сохраняем/не отправляем
      if (formData.testRecording?.recordId) {
        // игнорируем, не шлём
      }
      ['question1','question2','question3','question4'].forEach(key => {
        const rec = formData.audioAnswers[key];
        if (rec?.recordId) {
          recordIds.push(rec.recordId);
        } else if (rec?.blob) {
          form.append(key, rec.blob, `${key}.webm`);
        }
      });
      if (recordIds.length) {
        form.append('records', JSON.stringify(recordIds));
      }
      // текстовые ответы анкеты
      const answers = {
        gender: formData.gender,
        residenceCountry: formData.residenceCountry,
        country: formData.country,
        education: formData.education,
        adultExperience: formData.adultExperience,
        childrenExperience: formData.childrenExperience,
        itExperience: formData.itExperience,
        itEnglishExperience: formData.itEnglishExperience,
        groupTeachingExperience: formData.groupTeachingExperience,
        teachingStartDate: formData.teachingStartDate,
        birthDate: formData.birthDate,
        egeExperience: formData.egeExperience,
        certificates: formData.certificates,
        russianBankCard: formData.russianBankCard,
      };
      form.append('answers', JSON.stringify(answers));

      await api.post('/auth/questionnaire/submit/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubmitStatus('success');
      // Удаляем токен анкеты после успешной отправки
      sessionStorage.removeItem('questionnaire_token');
      delete api.defaults.headers.common['Authorization'];
    } catch (e) {
      console.error('Ошибка отправки анкеты', e);
      setSubmitStatus('error');
      setSubmitError(e.response?.data?.error || e.message || 'Не удалось отправить анкету. Попробуйте еще раз.');
    }
  };

  const renderStep1 = () => (
    <div className="questionnaire-step">
      <div className="questionnaire-header">
        <h1>Расскажите о себе</h1>
        <p className="questionnaire-intro">
          Давайте начнем знакомство 😊 Это поможет нам определить подходящее вам направление преподавания и затем найти учеников, с которыми вам будет интересно заниматься
        </p>
        {isAuthenticated && (
          <p className="questionnaire-edit-link">
            <span 
              onClick={() => navigate('/settings?section=profile')}
              style={{ cursor: 'pointer', color: '#667eea', textDecoration: 'underline' }}
            >
              Изменить данные о себе
            </span>
          </p>
        )}
      </div>

      <form className="questionnaire-form">
        <div className="form-group">
          <label>Укажите ваш пол</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="gender"
                value="male"
                checked={formData.gender === 'male'}
                onChange={handleChange}
                required
              />
              <span>Мужской</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="gender"
                value="female"
                checked={formData.gender === 'female'}
                onChange={handleChange}
                required
              />
              <span>Женский</span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Укажите страну проживания</label>
          <select
            name="residenceCountry"
            value={formData.residenceCountry}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Страна проживания</option>
            <option value="RU">Россия</option>
            <option value="US">США</option>
            <option value="GB">Великобритания</option>
            {/* Добавить больше стран */}
          </select>
        </div>

        <div className="form-group">
          <label>Укажите ваше гражданство</label>
          <select
            name="country"
            value={formData.country}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Гражданство</option>
            <option value="RU">Россия</option>
            <option value="US">США</option>
            <option value="GB">Великобритания</option>
            <option value="DE">Германия</option>
            <option value="FR">Франция</option>
            <option value="IT">Италия</option>
            <option value="ES">Испания</option>
            <option value="CN">Китай</option>
            <option value="JP">Япония</option>
            <option value="KR">Южная Корея</option>
            <option value="IN">Индия</option>
            <option value="AU">Австралия</option>
            <option value="BR">Бразилия</option>
            <option value="MX">Мексика</option>
            <option value="NL">Нидерланды</option>
            <option value="BE">Бельгия</option>
            <option value="CH">Швейцария</option>
            <option value="SE">Швеция</option>
            <option value="NO">Норвегия</option>
            <option value="DK">Дания</option>
            <option value="FI">Финляндия</option>
            <option value="PL">Польша</option>
            <option value="CZ">Чехия</option>
            <option value="HU">Венгрия</option>
            <option value="GR">Греция</option>
            <option value="PT">Португалия</option>
            <option value="IE">Ирландия</option>
            <option value="CY">Кипр</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Какое образование вы получили?
            <span className="help-icon">?</span>
          </label>
          <select
            name="education"
            value={formData.education}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Образование</option>
            <option value="higher">Высшее</option>
            <option value="incomplete_higher">Неоконченное высшее</option>
            <option value="secondary">Среднее</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            У вас есть опыт работы со взрослыми учениками?
            <span className="help-icon">?</span>
          </label>
          <select
            name="adultExperience"
            value={formData.adultExperience}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Опыт работы со взрослыми учениками</option>
            <option value="yes">Да</option>
            <option value="no">Нет</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            У вас есть опыт преподавания детям?
            <span className="help-icon">?</span>
          </label>
          <select
            name="childrenExperience"
            value={formData.childrenExperience}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Опыт преподавания детям</option>
            <option value="yes">Да</option>
            <option value="no">Нет</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Имеется ли у вас образование, опыт работы, написания статей или подготовки выступлений в сфере IT?
          </label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="itExperience"
                value="yes"
                checked={formData.itExperience === 'yes'}
                onChange={handleChange}
                required
              />
              <span>Да</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="itExperience"
                value="no"
                checked={formData.itExperience === 'no'}
                onChange={handleChange}
                required
              />
              <span>Нет</span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>
            Есть ли у вас опыт преподавания АЯ в сфере IT или подготовки студентов к собеседованию в иностранную IT-компанию?
          </label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="itEnglishExperience"
                value="yes"
                checked={formData.itEnglishExperience === 'yes'}
                onChange={handleChange}
                required
              />
              <span>Да</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="itEnglishExperience"
                value="no"
                checked={formData.itEnglishExperience === 'no'}
                onChange={handleChange}
                required
              />
              <span>Нет</span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>
            У вас есть опыт преподавания в группе?
            <span className="help-icon">?</span>
          </label>
          <select
            name="groupTeachingExperience"
            value={formData.groupTeachingExperience}
            onChange={handleChange}
            required
            className="form-select"
          >
            <option value="">Опыт преподавания в группе</option>
            <option value="yes">Да</option>
            <option value="no">Нет</option>
          </select>
        </div>

        <div className="form-group">
          <label>Дата начала преподавания/репетиторства</label>
          <input
            type="date"
            name="teachingStartDate"
            value={formData.teachingStartDate}
            onChange={handleChange}
            required
            className="form-date"
            max={maxTeachingDate}
          />
        </div>

        <div className="form-group">
          <label>Укажите дату вашего рождения</label>
          <input
            type="date"
            name="birthDate"
            value={formData.birthDate}
            onChange={handleChange}
            required
            className="form-date"
            max={maxBirthDate}
          />
        </div>

        <div className="form-group">
          <label>
            У вас есть опыт подготовки к ЕГЭ?
            <span className="help-icon">?</span>
          </label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="egeExperience"
                value="yes"
                checked={formData.egeExperience === 'yes'}
                onChange={handleChange}
                required
              />
              <span>Да</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="egeExperience"
                value="no"
                checked={formData.egeExperience === 'no'}
                onChange={handleChange}
                required
              />
              <span>Нет</span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Отметьте сертификаты, которые у вас есть</label>
          <div className="certificate-buttons">
            {['IELTS', 'TKT', 'TOEFL', 'none'].map(cert => (
              <button
                key={cert}
                type="button"
                className={`certificate-btn ${formData.certificates.includes(cert) ? 'active' : ''}`}
                onClick={() => {
                  const newCerts = formData.certificates.includes(cert)
                    ? formData.certificates.filter(c => c !== cert)
                    : [...formData.certificates, cert];
                  setFormData(prev => ({ ...prev, certificates: newCerts }));
                }}
              >
                {cert === 'none' ? 'Другое' : cert}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Прикрепите свое CV (до 10 МБ)</label>
          <div className="cv-upload-wrapper">
            <label className="cv-upload-button">
              <input
                type="file"
                name="cv"
                accept=".pdf,.doc,.docx,.rtf"
                onChange={handleChange}
                className="cv-file-input"
              />
              <span className="cv-upload-text">
                {formData.cv ? 'Изменить файл' : 'Выбрать файл'}
              </span>
            </label>
            {formData.cv && (
              <div className="cv-file-name">{formData.cv.name}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>
            Есть ли у вас возможность получать вознаграждение на карту российского банка?
            <span className="help-icon">?</span>
          </label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="russianBankCard"
                value="yes"
                checked={formData.russianBankCard === 'yes'}
                onChange={handleChange}
                required
              />
              <span>Да</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="russianBankCard"
                value="no"
                checked={formData.russianBankCard === 'no'}
                onChange={handleChange}
                required
              />
              <span>Нет</span>
            </label>
          </div>
        </div>

        <button 
          type="button" 
          onClick={handleNext} 
          className={`btn-primary ${!isStep1Valid() ? 'btn-disabled' : ''}`}
          disabled={!isStep1Valid()}
        >
          Отправить
        </button>
      </form>
    </div>
  );

  const renderStep2 = () => (
    <div className="questionnaire-step">
      <div className="audio-test-header">
        <h1>Сделайте пробную запись</h1>
        <div className="audio-task">
          <div className="task-title">Задание</div>
          <div className="task-body">
            Нажмите «Начать видеозапись», произнесите пару фраз, затем остановите и прослушайте/просмотрите запись. 
            Убедитесь, что вас видно и хорошо слышно: без шумов и эха, нормальный уровень громкости.
          </div>
        </div>
      </div>

      <AudioRecorder
        mode="video"
        onRecordingComplete={(blob, isValid) => handleAudioComplete(blob, isValid)}
        minDuration={10}
        maxDuration={120}
      />

      <div className="questionnaire-actions">
        <button
          type="button"
          onClick={handleNext}
          disabled={!formData.testRecording || !formData.testRecording.isValid}
          className={`btn-next ${formData.testRecording && formData.testRecording.isValid ? 'btn-next-active' : ''}`}
        >
          Далее
        </button>
      </div>
    </div>
  );

  const renderAudioQuestion = (questionNumber, questionText, instructionText) => {
    const questionKey = `question${questionNumber}`;
    const hasValidAnswer = formData.audioAnswers[questionKey] && formData.audioAnswers[questionKey].isValid;

    return (
      <div className="questionnaire-step">
        <div className="question-header">
          <span className="question-number">Вопрос {questionNumber} из 4</span>
          <h2>Запишите видео-ответ на английском языке</h2>
          <p className="question-instruction">
            Максимальная длина записи — 2 минуты. Вы можете переснять запись перед отправкой. Вас должно быть хорошо видно и слышно, иначе мы не сможем оценить ваши ответы
          </p>
        </div>

        <div className="question-box">
          <h3>Вопрос, на который нужно ответить</h3>
          <div className="question-text" dangerouslySetInnerHTML={{ __html: questionText }} />
        </div>

        <AudioRecorder
          mode="video"
          onRecordingComplete={(blob, isValid) => handleAudioComplete(blob, isValid, questionKey)}
          minDuration={10}
          maxDuration={120}
        />

        <div className="questionnaire-actions">
          <button
            type="button"
            onClick={handleNext}
            disabled={!hasValidAnswer}
            className={`btn-next ${hasValidAnswer ? 'btn-next-active' : ''}`}
          >
            Далее
          </button>
        </div>
      </div>
    );
  };

  const renderStep7 = () => {
    let content;
    
    if (submitStatus === 'loading') {
      content = (
        <>
          <div className="final-icon">🚀</div>
          <h1>Спасибо!</h1>
          <p className="final-message">Сохраняем ваши ответы...</p>
          <p className="final-submessage">Пожалуйста, подождите пару минут</p>
          <p className="final-email-message">
            Мы сообщим вам на почту о результатах рассмотрения вашей анкеты.
          </p>
        </>
      );
    } else if (submitStatus === 'success') {
      content = (
        <>
          <div className="success-icon-circle">
            <svg className="success-checkmark" viewBox="0 0 52 52">
              <circle className="success-checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
              <path className="success-checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>
          <h1 className="success-title">Спасибо!</h1>
          <p className="success-message">
            Ваша анкета успешно отправлена. Мы сообщим вам на почту о результатах рассмотрения вашей анкеты.
          </p>
        </>
      );
    } else if (submitStatus === 'error') {
      content = (
        <>
          <div className="final-icon">❌</div>
          <h1>Ошибка</h1>
          <p className="final-message">{submitError}</p>
          <button 
            type="button" 
            onClick={handleSubmit} 
            className="btn-primary"
            style={{ marginTop: '1rem' }}
          >
            Попробовать еще раз
          </button>
        </>
      );
    } else {
      // Начальное состояние (не должно показываться, но на всякий случай)
      content = (
        <>
          <div className="final-icon">🚀</div>
          <h1>Спасибо!</h1>
          <p className="final-message">Сохраняем ваши ответы...</p>
        </>
      );
    }
    
    return (
      <div className="questionnaire-step final-step">
        {content}
      </div>
    );
  };

  return (
    <div className="questionnaire-page">
      <Header forceUnauthenticated={true} />
      <div className="questionnaire-container">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderAudioQuestion(
          1,
          `<p><strong>1. Hello and welcome to this interview. Firstly, let's warm up - tell us about your:</strong></p><ul class="question-list"><li>Age</li><li>Place of living</li><li>Education and field of study</li><li>Teaching experience</li></ul><p>Students of what ages have you worked with? How long?</p>`
        )}
        {currentStep === 4 && renderAudioQuestion(
          2,
          `<p><strong>2. In your opinion, what are the differences in teaching teenagers vs. young learners?</strong></p><p>What makes the lesson effective for a 6-year-old, 12-year-old, and 16-year-old?</p><p>Have you taught the students of these age groups?</p>`
        )}
        {currentStep === 5 && renderAudioQuestion(
          3,
          `<p><strong>3. Do you have the experience of:</strong></p><ul class="question-list"><li>State/national exams preparation? If yes, please share the results.</li><li>English for specific purposes teaching? (e.g. Business English, Medical, IT, etc.)</li><li>Work experience in other professional fields?</li></ul><p>Have you taken official language proficiency tests such as IELTS (TOEFL, CAE, CPE etc)? Do you have a certification that confirms your English language proficiency?</p>`
        )}
        {currentStep === 6 && renderAudioQuestion(
          4,
          `<p><strong>4. Thank you for your answers!</strong></p><p>Пожалуйста, поделитесь вашими планами на сотрудничество с Roselearn.me: когда вам было бы удобно приступить к занятиям?</p>`
        )}
        {currentStep === 7 && renderStep7()}
      </div>
      <Footer />
    </div>
  );
};

export default TeacherQuestionnaire;

